import { MuiTelInput, MuiTelInputCountry } from 'mui-tel-input';
import { Controller, Control, useController } from 'react-hook-form';
import { useMediaQuery, useTheme, Box, Typography, Link, Checkbox } from '@mui/material';
import { useState, type ReactElement } from 'react';
import { useGetOrganizations } from '@/apis/agency/organizations/hook';
import { getPhoneCountry } from '@/utils/isUsPhoneNumber';

interface Props extends Omit<any, 'name'> {
  name: string;
  control: Control<any>;
  consent_key?: string;
}

type ConsentBoxProps = {
  name: string;
  control: Control<any>;
  isFocused: boolean;
  brandName: string;
  termsUrl?: string | null;
  privacyUrl?: string | null;
  linkSx: { color: string; fontWeight: number };
};

/** Separate component so consent Controller always returns ReactElement (tsc). */
function SmsConsentBox({
  name,
  control,
  isFocused,
  brandName,
  termsUrl,
  privacyUrl,
  linkSx
}: ConsentBoxProps): ReactElement | null {
  const {
    field: consentField,
    fieldState: { error: consentError }
  } = useController({ name, control });
  const theme = useTheme();

  // Visible while focused, already consented, or when validation failed
  const showPrivacy = isFocused || !!consentField.value || !!consentError;
  if (!showPrivacy) return null;

  return (
    <Box>
      <Box
        onMouseDown={(e) => e.preventDefault()}
        sx={{
          mt: '-1px',
          borderRadius: '0 0 4px 4px',
          backgroundColor: 'primary.light',
          border: isFocused || consentError ? '2px solid' : '1px solid',
          borderColor: consentError ? theme.palette.error.main : isFocused ? theme.palette.primary.main : '#e5e7eb',
          borderTop: 'none',
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1
        }}
      >
        <Checkbox
          size='small'
          checked={!!consentField.value}
          onChange={(_, checked) => consentField.onChange(checked)}
          sx={{ p: 0, mt: '1px' }}
        />

        <Typography
          sx={{
            fontSize: 12,
            color: '#777777',
            lineHeight: 1.4
          }}
        >
          I confirm I am authorized to receive SMS notifications at this number. I agree to receive automated,
          transactional SMS notifications from {brandName} regarding AI agent activity and appointment updates. Message
          frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help. Your consent is subject to
          our{' '}
          {termsUrl ? (
            <Link href={termsUrl} target='_blank' rel='noopener noreferrer' underline='none' sx={linkSx}>
              Terms of Service
            </Link>
          ) : (
            <Box component='span' sx={linkSx}>
              Terms of Service
            </Box>
          )}{' '}
          and{' '}
          {privacyUrl ? (
            <Link href={privacyUrl} target='_blank' rel='noopener noreferrer' underline='none' sx={linkSx}>
              Privacy Policy
            </Link>
          ) : (
            <Box component='span' sx={linkSx}>
              Privacy Policy
            </Box>
          )}
          .
        </Typography>
      </Box>
      {consentError?.message && (
        <Typography color='error' fontSize={12} sx={{ mt: 0.5, ml: 0.5 }}>
          {consentError.message}
        </Typography>
      )}
    </Box>
  );
}

export default function TelInput({ name, control, rules, consent_key, ...textFieldProps }: Props) {
  const theme = useTheme();
  const { data: organization } = useGetOrganizations();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const { defaultCountry, ...muiTelInputProps } = textFieldProps;
  const size = muiTelInputProps.size || (isLargeScreen ? 'medium' : 'small');

  const [isFocused, setIsFocused] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<MuiTelInputCountry | null>(
    (defaultCountry as MuiTelInputCountry) ?? null
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }): ReactElement => {
        const termsUrl = organization?.configuration?.terms_and_conditions_url;
        const privacyUrl = organization?.configuration?.privacy_policy_url;
        const linkSx = { color: theme.palette.primary.main, fontWeight: 500 };
        const brandName = organization?.name ?? 'my agency';
        // Prefer parsed country so CA (+1) is not treated as US; fall back to picker while typing
        const phoneCountry = getPhoneCountry(field.value);
        const isUsNumber = phoneCountry ? phoneCountry === 'US' : selectedCountry === 'US';
        const attachConsent = !!consent_key && isUsNumber && isFocused;

        return (
          <Box>
            <MuiTelInput
              {...field}
              {...muiTelInputProps}
              preferredCountries={muiTelInputProps.preferredCountries ?? ['US', 'CA', 'GB', 'AU', 'IN']}
              defaultCountry={defaultCountry}
              forceCallingCode
              size={size}
              fullWidth
              autoComplete='off'
              onChange={(value, info) => {
                setSelectedCountry(info.countryCode);
                // Keep E.164 (+country…) so FE can derive country_code on save.
                field.onChange(value.replace(/\s+/g, ''));
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                setIsFocused(false);
                field.onBlur();
              }}
              error={!!error}
              helperText={error ? error.message : null}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: attachConsent ? '4px 4px 0 0' : '4px',
                  backgroundColor: '#fff',
                  '& fieldset': {
                    borderColor: isFocused ? 'primary' : '#e5e7eb',
                    borderWidth: isFocused ? '2px' : '1px',
                    borderBottom: attachConsent ? 'none' : undefined
                  }
                }
              }}
            />

            {!!consent_key && isUsNumber ? (
              <SmsConsentBox
                name={consent_key}
                control={control}
                isFocused={isFocused}
                brandName={brandName}
                termsUrl={termsUrl}
                privacyUrl={privacyUrl}
                linkSx={linkSx}
              />
            ) : null}
          </Box>
        );
      }}
    />
  );
}
